import { Box, ThemeProvider, styled } from "@mui/material";

import { MaterialDesignContent, SnackbarProvider } from "notistack";
import React, { useEffect } from "react";

import { ErrorBoundary } from "./components";
import {
  CertificateMethod,
  FailPage,
  Loading,
  MissingIntegrationId,
  Processing,
  SelectMethod,
  SignatureMethod,
  SuccessPage,
  TokenMethod,
  TrustedProfileMethod,
} from "./pages";
import { useStore } from "./store";
import { theme } from "./theme";

export const App: React.FC = () => {
  const { setIntegrationId, isInitialized, integrationMethod, integration, initialize } = useStore();
  const integrationIdFromStore = useStore((s) => s.integrationId);
  const urlParams = new URLSearchParams(window.location.search);
  const integrationIdInParams = urlParams.get("integration-id") ?? undefined;
  // Use store id, or URL param so correct view and timer work on refresh before store is hydrated
  const integrationId = integrationIdFromStore ?? integrationIdInParams;

  // Sync integration-id from URL into store so it's available for initialize and persists
  useEffect(() => {
    if (integrationIdInParams && integrationIdInParams !== integrationIdFromStore) {
      setIntegrationId(integrationIdInParams);
    }
  }, [integrationIdInParams, integrationIdFromStore, setIntegrationId]);

  // Initialize only after the id is in the store (e.g. after URL is synced by the effect above).
  useEffect(() => {
    if (integrationIdFromStore) {
      initialize();
    }
  }, [integrationIdFromStore]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderState = () => {
    if (!integrationId || integrationId === "") {
      return <MissingIntegrationId />;
    }

    if (!isInitialized) {
      return <Loading />;
    }

    /**
     *  Possible states of integration
     *   Active = "Active",
     *   Created = "Created",
     *   Invalid = "Invalid",
     *   SessionSignedRequestGenerated = "SessionSignedRequestGenerated",
     *   SessionSignedRequestProvided = "SessionSignedRequestProvided",
     *   TokenProvided = "TokenProvided",
     */
    if (integration?.state === "Invalid") {
      return <FailPage />;
    }

    if (integration?.state === "Active") {
      return <SuccessPage />;
    }

    if (integration?.state === "SessionSignedRequestProvided" || integration?.state === "TokenProvided") {
      return <Processing />;
    }

    if (
      (integration?.state === "Created" || integration?.state === "SessionSignedRequestGenerated") &&
      !integrationMethod
    ) {
      return <SelectMethod />;
    }

    if (
      (integration?.state === "Created" || integration?.state === "SessionSignedRequestGenerated") &&
      integrationMethod === "TrustedProfile"
    ) {
      return <TrustedProfileMethod />;
    }

    if (
      (integration?.state === "Created" || integration?.state === "SessionSignedRequestGenerated") &&
      integrationMethod === "Token"
    ) {
      return <TokenMethod />;
    }

    if (
      (integration?.state === "Created" || integration?.state === "SessionSignedRequestGenerated") &&
      integrationMethod === "Signature"
    ) {
      return <SignatureMethod />;
    }

    if (
      (integration?.state === "Created" || integration?.state === "SessionSignedRequestGenerated") &&
      integrationMethod === "Certificate"
    ) {
      return <CertificateMethod />;
    }

    return <FailPage />;
  };

  const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
    "&.notistack-MuiContent-error": {
      fontFamily: "Helvetica",
    },
  }));

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <SnackbarProvider
          Components={{ error: StyledMaterialDesignContent }}
          maxSnack={3}
          autoHideDuration={3000}
          variant="error"
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box mt="42px">{renderState()}</Box>
        </SnackbarProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
